"use client";

import { Moon, Sun } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

import { cn } from "@/lib/utils";
import { useTheme } from "@/utils/themeProvider";

interface AnimatedThemeTogglerProps
	extends React.ComponentPropsWithoutRef<"button"> {
	duration?: number;
	size?: "sm" | "md" | "lg";
}

const sizeConfig = {
	sm: { button: "w-8 h-8", icon: "w-4 h-4" },
	md: { button: "w-10 h-10", icon: "w-5 h-5" },
	lg: { button: "w-12 h-12", icon: "w-6 h-6" },
};

export function AnimatedThemeToggler({
	className,
	duration = 400,
	size = "sm",
	...props
}: AnimatedThemeTogglerProps) {
	const { theme, setTheme } = useTheme();
	const [mounted, setMounted] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);

	// Avoid hydration mismatch by only rendering theme-dependent UI after mount
	useEffect(() => {
		setMounted(true);
	}, []);

	const isDark = theme === "dark";

	const { button, icon } = sizeConfig[size];

	const toggleTheme = useCallback(async () => {
		if (!buttonRef.current) return;

		const newTheme = isDark ? "light" : "dark";

		// Check if View Transitions API is supported
		if (!document.startViewTransition) {
			setTheme(newTheme);
			return;
		}

		await document.startViewTransition(() => {
			flushSync(() => {
				setTheme(newTheme);
			});
		}).ready;

		const { top, left, width, height } =
			buttonRef.current.getBoundingClientRect();
		const x = left + width / 2;
		const y = top + height / 2;
		const maxRadius = Math.hypot(
			Math.max(left, window.innerWidth - left),
			Math.max(top, window.innerHeight - top),
		);

		document.documentElement.animate(
			{
				clipPath: [
					`circle(0px at ${x}px ${y}px)`,
					`circle(${maxRadius}px at ${x}px ${y}px)`,
				],
			},
			{
				duration,
				easing: "ease-in-out",
				pseudoElement: "::view-transition-new(root)",
			},
		);
	}, [isDark, duration, setTheme]);

	return (
		<button
			ref={buttonRef}
			type="button"
			onClick={toggleTheme}
			className={cn(
				button,
				"relative inline-flex items-center justify-center overflow-hidden rounded-md",
				"border border-border text-foreground",
				"hover:bg-muted",
				"focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
				"transition-colors duration-150",
				className,
			)}
			aria-label={
				mounted
					? isDark
						? "Switch to light mode"
						: "Switch to dark mode"
					: "Toggle theme"
			}
			{...props}
		>
			{/* Keep a consistent icon footprint and crossfade between states */}
			<Sun
				className={cn(
					icon,
					"absolute transition-all duration-200",
					mounted && isDark
						? "opacity-100 rotate-0 scale-100"
						: "opacity-0 -rotate-90 scale-75",
				)}
			/>
			<Moon
				className={cn(
					icon,
					"absolute transition-all duration-200",
					mounted && isDark
						? "opacity-0 rotate-90 scale-75"
						: "opacity-100 rotate-0 scale-100",
				)}
			/>
			<span className="sr-only">Toggle theme</span>
		</button>
	);
}
